import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { moveRowsToInboxPlusPivot } from '../actions/moveRowsToInboxPlusPivot';
import { orchestrator } from 'satcheljs';
import {
    listViewStore,
    getRowIdsFromRowKeys,
    MailFolderTableQuery,
    isItemOfMessageType,
    getApplyConversationActionContext,
} from 'owa-mail-list-store';
import getHashtagForInboxClassificationFilter from 'owa-mail-inbox-plus-store/lib/selectors/getHashtagForInboxClassificationFilter';
import InferenceClassification from 'owa-service/lib/contract/InferenceClassification';
import updateItemHashtagsService from '../services/updateItemHashtagsService';
import { mailStore } from 'owa-mail-store';
import Message from 'owa-service/lib/contract/Message';
import deleteItemsStoreUpdate from 'owa-mail-actions/lib/triage/deleteItemsStoreUpdate';
import { actionNames, DeleteConversationStoreUpdate } from 'owa-mail-store-actions';
import { raiseAction } from 'satcheljs/lib/legacy/stitch';
import { updateConversationHashtagsService } from '../services/updateConversationHashtagsService';
import InboxClassificationFilter from 'owa-mail-inbox-plus-store/lib/store/schema/InboxClassificationFilter';
import processApplyConversationActionResponseMessage from 'owa-mail-triage-action-response-processor/lib/processApplyConversationActionResponseMessage';
import onTriageActionCompleted from 'owa-mail-triage-action-response-processor/lib/onTriageActionCompleted';
import onTriageActionFailed from 'owa-mail-triage-action-response-processor/lib/onTriageActionFailed';
import handleServerResponseSuccessAndError from 'owa-mail-triage-action-response-processor/lib/handleServerResponseSuccessAndError';
import * as undoActions from 'owa-mail-undo';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import loc, { format } from 'owa-localize';
import { undoAction } from 'owa-locstrings/lib/strings/undoaction.locstring.json';
import { MailIcons } from 'owa-mail-icons';
import { getListViewNotificationDimensions } from 'owa-mail-layout';
import { lazyShowNotification, NotificationBarCallbackReason } from 'owa-notification-bar';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { moveItemsToPivotSucceeded } from './moveRowsToInboxPlusPivotOrchestrator.locstring.json';
import getInboxPlusPivotDisplayProperties from 'owa-mail-inbox-plus-store/lib/selectors/getInboxPlusPivotDisplayProperties';

orchestrator(moveRowsToInboxPlusPivot, actionMessage => {
    let inferenceClassificationFilterToWrite;
    let originalInferenceClassificationFilter;
    const tableView = listViewStore.tableViews.get(actionMessage.tableViewId);
    const tableQuery = tableView.tableQuery as MailFolderTableQuery;

    // If user is moving to F/O we need to update the classificaiton filter as well as the hashtag properties
    if (
        actionMessage.destinationPivot === InboxClassificationFilter.Focused ||
        actionMessage.destinationPivot === InboxClassificationFilter.Other
    ) {
        inferenceClassificationFilterToWrite =
            actionMessage.destinationPivot === InboxClassificationFilter.Focused
                ? 'Focused'
                : 'Other';
    }

    if (tableQuery.focusedViewFilter !== FocusedViewFilter.None) {
        originalInferenceClassificationFilter =
            tableQuery.focusedViewFilter === FocusedViewFilter.Focused ? 'Focused' : 'Other';
    }

    const hashtagToWrite = getHashtagForInboxClassificationFilter(actionMessage.destinationPivot);
    const hashtagBaseToWrite = getHashtagForInboxClassificationFilter(
        tableQuery.inboxClassificationFilter
    );

    switch (tableView.tableQuery.listViewType) {
        case ReactListViewType.Conversation:
            return moveConversationsToInboxPlusPivotInternal(
                actionMessage.rowKeys,
                hashtagToWrite,
                hashtagBaseToWrite,
                actionMessage.tableViewId,
                inferenceClassificationFilterToWrite,
                originalInferenceClassificationFilter,
                actionMessage.destinationPivot
            );
        case ReactListViewType.Message:
            return moveItemsToInboxPlusPivotInternal(
                actionMessage.rowKeys,
                hashtagToWrite,
                hashtagBaseToWrite,
                actionMessage.tableViewId,
                inferenceClassificationFilterToWrite,
                originalInferenceClassificationFilter,
                actionMessage.destinationPivot
            );
        default:
            throw new Error('Invalid list view type');
    }
});

// Orchestrator to remove the row locally and make a call to UpdateItem to update the hashtag properties
function moveItemsToInboxPlusPivotInternal(
    rowKeys: string[],
    hashtag: string,
    hashtagBase: string,
    tableViewId: string,
    inferenceClassificationFilter: InferenceClassification,
    originalInferenceClassificationFilter: InferenceClassification,
    destinationPivot: InboxClassificationFilter
): Promise<void> {
    const itemIds = getRowIdsFromRowKeys(rowKeys, tableViewId);
    const tableView = listViewStore.tableViews.get(tableViewId);

    const itemContexts = itemIds.map(itemIdValue => {
        const item = mailStore.items.get(itemIdValue);
        let isReadValue;

        if (isItemOfMessageType(item)) {
            isReadValue = (item as Message).IsRead;
        } else {
            // Default to isRead = true when not a mail item
            isReadValue = true;
        }

        return {
            itemId: itemIdValue,
            itemConversationId: item.ConversationId,
            isRead: isReadValue,
            mailboxInfo: item.MailboxInfo,
        };
    });

    deleteItemsStoreUpdate(itemContexts, tableView.tableQuery.folderId);

    const updateItemHashtagPromise = updateItemHashtagsService(
        itemIds,
        hashtag,
        hashtagBase,
        inferenceClassificationFilter
    );

    undoActions.addActionToUndoStack(async () => {
        const response = await updateItemHashtagPromise;
        if (response.ResponseClass == 'Success') {
            return updateItemHashtagsService(
                itemIds,
                hashtagBase,
                hashtag,
                originalInferenceClassificationFilter
            );
        }
        return Promise.resolve();
    }, tableView.serverFolderId);

    // Show notification bar immediately after client action
    showNotificationOnPivotMove(destinationPivot);

    return updateItemHashtagPromise
        .then(response => {
            onTriageActionCompleted(
                rowKeys,
                tableView.tableQuery.folderId,
                false /* shouldRemoveRowsFromTombstone */
            );

            return handleServerResponseSuccessAndError(response);
        })
        .catch(error => {
            onTriageActionCompleted(
                rowKeys,
                tableView.tableQuery.folderId,
                false /* shouldRemoveRowsFromTombstone */
            );
            onTriageActionFailed(tableView);
            return Promise.reject(error);
        });
}

// Orchestrator to remove the row locally and make a call to ApplyConversationAction to update the hashtag properties
function moveConversationsToInboxPlusPivotInternal(
    rowKeys: string[],
    hashtag: string,
    hashtagBase: string,
    tableViewId: string,
    inferenceClassificationFilter: InferenceClassification,
    originalInferenceClassificationFilter: InferenceClassification,
    destinationPivot: InboxClassificationFilter
): Promise<void> {
    const conversationActionContexts = rowKeys.map(rowKey =>
        getApplyConversationActionContext(rowKey, tableViewId)
    );
    const tableView = listViewStore.tableViews.get(tableViewId);
    const conversationIds = conversationActionContexts.map(
        conversationContext => conversationContext.conversationId
    );

    raiseAction<DeleteConversationStoreUpdate>(actionNames.DeleteConversationStoreUpdate)(
        conversationIds,
        tableView
    );

    const updateConversationHashtagResponse = updateConversationHashtagsService(
        conversationActionContexts,
        hashtag,
        hashtagBase,
        tableView.tableQuery.folderId,
        inferenceClassificationFilter
    );

    undoActions.addActionToUndoStack(async () => {
        const response = await updateConversationHashtagResponse;
        if (response.ResponseClass == 'Success') {
            return updateConversationHashtagsService(
                conversationActionContexts,
                hashtagBase,
                hashtag,
                tableView.tableQuery.folderId,
                originalInferenceClassificationFilter
            );
        }
        return Promise.resolve();
    }, tableView.serverFolderId);

    // Show notification bar immediately after client action
    showNotificationOnPivotMove(destinationPivot);

    return processApplyConversationActionResponseMessage(
        updateConversationHashtagResponse,
        rowKeys,
        tableView.tableQuery.folderId,
        tableView,
        true /* shouldRemoveRowsFromTombstone */
    );
}

function showNotificationOnPivotMove(destinationPivot: InboxClassificationFilter) {
    if (!getUserConfiguration().UserOptions.ShowNotificationBar) {
        return;
    }

    const destinationPivotText = getInboxPlusPivotDisplayProperties(destinationPivot).displayText;
    const notificationDimensions = getListViewNotificationDimensions();

    lazyShowNotification.importAndExecute(
        'moveOrArchiveNotification',
        'MailModuleNotificationBarHost',
        format(loc(moveItemsToPivotSucceeded), destinationPivotText),
        {
            icon: MailIcons.FabricMovetoFolder,
            primaryActionText: loc(undoAction),
            allowClear: true,
            // Want 12 px of padding on either side of the notificaiton bar
            minWidth: notificationDimensions.minWidth,
            maxWidth: notificationDimensions.maxWidth,
            notificationCallback: reason => {
                if (reason === NotificationBarCallbackReason.PrimaryActionClicked) {
                    undoActions.lazyUndo.importAndExecute('NotificationBar');
                }
            },
        }
    );
}
